import type { MoneyAmount } from './payment';

export const CREDIT_DECLINE_BREAKDOWN_TYPE = {
  INCOME: 'INCOME',
  EXTERNAL_OBLIGATIONS: 'EXTERNAL_OBLIGATIONS',
  INTERNAL_OBLIGATIONS: 'INTERNAL_OBLIGATIONS',
  EXPENSES: 'EXPENSES',
} as const;

export type CreditDeclineBreakdownType =
  (typeof CREDIT_DECLINE_BREAKDOWN_TYPE)[keyof typeof CREDIT_DECLINE_BREAKDOWN_TYPE];

export interface CreditDeclineBreakdownAmount extends MoneyAmount {
  amountMax?: number;
}

export interface CreditDeclineBreakdownItem {
  type: CreditDeclineBreakdownType;
  amount: CreditDeclineBreakdownAmount;
}

/** Display order when multiple breakdown rows are present. */
export const CREDIT_DECLINE_BREAKDOWN_ORDER: readonly CreditDeclineBreakdownType[] =
  [
    CREDIT_DECLINE_BREAKDOWN_TYPE.INCOME,
    CREDIT_DECLINE_BREAKDOWN_TYPE.EXTERNAL_OBLIGATIONS,
    CREDIT_DECLINE_BREAKDOWN_TYPE.INTERNAL_OBLIGATIONS,
    CREDIT_DECLINE_BREAKDOWN_TYPE.EXPENSES,
  ];

/** Item under credit_decline_explainer.external_obligations.items (future phases). */
export interface CreditDeclineExternalObligationItem {
  name: string;
  type: string;
  status: string;
  monthlyInstalment: MoneyAmount;
}

/**
 * Action types under credit_decline_explainer.actions. Each maps to a breakdown
 * row (see ACTION_TYPE_TO_BREAKDOWN_TYPE) and carries a deeplink the customer can
 * tap to recover (connect bank, pay dues, fill questionnaire).
 */
export const CREDIT_DECLINE_ACTION_TYPE = {
  LEANTECH_CONNECTION: 'LEANTECH_CONNECTION',
  PAY_DUES: 'PAY_DUES',
  QUESTIONNAIRE: 'QUESTIONNAIRE',
} as const;

export type CreditDeclineActionType =
  (typeof CREDIT_DECLINE_ACTION_TYPE)[keyof typeof CREDIT_DECLINE_ACTION_TYPE];

/** Parsed, validated action from credit_decline_explainer.actions. */
export interface CreditDeclineAction {
  type: CreditDeclineActionType;
  /** Validated https deeplink on the tamara.go.link host. */
  action: string;
  eligible: boolean | null;
  mandatory: boolean | null;
}

export interface CreditDeclineInfo {
  declineCode: string;
  declineDescription?: string;
  headline?: string;
  subtitle?: string;
  /** Null when unavailable — UI shows a dash in the comparison card. */
  orderAmount: MoneyAmount | null;
  /** Null when unavailable — UI shows a dash in the comparison card. */
  recommendedLimit: MoneyAmount | null;
  /** Only rows present in the API response, in {@link CREDIT_DECLINE_BREAKDOWN_ORDER}. */
  breakdown: CreditDeclineBreakdownItem[];
  /** Parsed from external_obligations.items; reserved for future detail UI. */
  externalObligationItems?: CreditDeclineExternalObligationItem[];
  /** Recovery actions (deeplinks) keyed off explainer.actions. Empty when none. */
  actions: CreditDeclineAction[];
}
